import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api, fileUrl, formatApiErrorDetail } from "@/lib/api";
import { useContentCtx } from "@/context/ContentContext";
import { LOGO_URL } from "@/data/homeContent";
import { ArrowLeft, Loader2, Save, Upload } from "lucide-react";
import { RichTextEditor } from "@/components/RichTextEditor";

const RICH_KEYS = new Set([
  "home_card1_body", "home_card2_body", "home_card3_body",
  "home_search_p1", "home_search_p2", "home_search_p3", "home_search_highlight",
  "home_multidevice_body",
  "about_editorial_body", "about_trusted_body", "about_experience_body",
  "listing_intro", "listing_double_desc", "listing_full_desc", "listing_half_desc", "listing_normal_desc",
]);

const FIELDS = [
  { group: "Hero Section", items: [
    { key: "hero_title", label: "Hero Title", type: "text" },
    { key: "hero_subtitle", label: "Hero Subtitle", type: "textarea" },
    { key: "hero_headline", label: "Hero Headline", type: "text" },
    { key: "hero_tagline", label: "Hero Tagline", type: "text" },
    { key: "hero_cta", label: "Primary Button Text", type: "text" },
    { key: "hero_secondary_cta", label: "Secondary Button Text", type: "text" },
    { key: "hero_badge_1", label: "Badge 1", type: "text" },
    { key: "hero_badge_2", label: "Badge 2", type: "text" },
    { key: "cover_image_url", label: "Directory Cover Image", type: "image" },
    { key: "hero_bg_image_url", label: "Hero Background Image", type: "image" },
    { key: "hero_bg_opacity", label: "Hero Background Visibility", type: "opacity" },
  ]},
  { group: "Digital Edition / Flipbook", items: [
    { key: "flipbook_url", label: "Flipbook (Heyzine) URL", type: "text" },
  ]},
  { group: "Home · Search Section", items: [
    { key: "home_search_title", label: "Section Title", type: "text" },
    { key: "home_search_p1", label: "Paragraph 1", type: "textarea" },
    { key: "home_search_p2", label: "Paragraph 2", type: "textarea" },
    { key: "home_search_p3", label: "Paragraph 3", type: "textarea" },
    { key: "home_search_highlight", label: "Highlighted Paragraph", type: "textarea" },
    { key: "home_search_cta", label: "Button Text", type: "text" },
  ]},
  { group: "Home · Promo Cards", items: [
    { key: "home_card1_title", label: "Card 1 Title", type: "text" },
    { key: "home_card1_body", label: "Card 1 Body", type: "textarea" },
    { key: "home_card1_cta", label: "Card 1 Button", type: "text" },
    { key: "home_card2_title", label: "Card 2 Title", type: "text" },
    { key: "home_card2_body", label: "Card 2 Body", type: "textarea" },
    { key: "home_card2_cta", label: "Card 2 Button", type: "text" },
    { key: "home_card3_title", label: "Card 3 Title", type: "text" },
    { key: "home_card3_body", label: "Card 3 Body", type: "textarea" },
    { key: "home_card3_cta", label: "Card 3 Button", type: "text" },
  ]},
  { group: "Home · Section Images", items: [
    { key: "home_search_image", label: "Search Section Image", type: "image" },
    { key: "home_card1_image", label: "Card 1 Image (List Your Boarding School)", type: "image" },
    { key: "home_card2_image", label: "Card 2 Image (Order Print Edition)", type: "image" },
    { key: "home_card3_image", label: "Card 3 Image (Open Digital Edition)", type: "image" },
    { key: "home_multidevice_image", label: "Multi-Device Section Image", type: "image" },
  ]},
  { group: "Home · Multi-Device Section", items: [
    { key: "home_multidevice_body", label: "Paragraphs (blank line between each)", type: "textarea", rows: 8 },
    { key: "home_pill_1", label: "Pill 1", type: "text" },
    { key: "home_pill_2", label: "Pill 2", type: "text" },
    { key: "home_pill_3", label: "Pill 3", type: "text" },
  ]},
  { group: "Home · Listing Opportunity", items: [
    { key: "home_opportunity_title", label: "Title", type: "text" },
    { key: "home_opportunity_intro", label: "Intro", type: "textarea" },
    { key: "home_feature1_title", label: "Feature 1 Title", type: "text" },
    { key: "home_feature1_body", label: "Feature 1 Body", type: "textarea" },
    { key: "home_feature2_title", label: "Feature 2 Title", type: "text" },
    { key: "home_feature2_body", label: "Feature 2 Body", type: "textarea" },
    { key: "home_feature3_title", label: "Feature 3 Title", type: "text" },
    { key: "home_feature3_body", label: "Feature 3 Body", type: "textarea" },
    { key: "home_feature4_title", label: "Feature 4 Title (highlighted)", type: "text" },
    { key: "home_feature4_body", label: "Feature 4 Body", type: "textarea" },
    { key: "home_feature5_title", label: "Feature 5 Title (highlighted)", type: "text" },
    { key: "home_feature5_body", label: "Feature 5 Body", type: "textarea" },
  ]},
  { group: "About · Hero & Intro", items: [
    { key: "about_intro", label: "Intro Paragraph", type: "textarea", rows: 5 },
    { key: "about_bg_image_url", label: "About Background Image", type: "image" },
    { key: "about_bg_opacity", label: "About Background Visibility", type: "opacity" },
  ]},
  { group: "About · Team", items: [
    { key: "about_team1_name", label: "Member 1 Name", type: "text" },
    { key: "about_team1_role", label: "Member 1 Role", type: "text" },
    { key: "about_team1_img", label: "Member 1 Photo", type: "image" },
    { key: "about_team2_name", label: "Member 2 Name", type: "text" },
    { key: "about_team2_role", label: "Member 2 Role", type: "text" },
    { key: "about_team2_img", label: "Member 2 Photo", type: "image" },
    { key: "about_team3_name", label: "Member 3 Name", type: "text" },
    { key: "about_team3_role", label: "Member 3 Role", type: "text" },
    { key: "about_team3_img", label: "Member 3 Photo", type: "image" },
    { key: "about_team4_name", label: "Member 4 Name", type: "text" },
    { key: "about_team4_role", label: "Member 4 Role", type: "text" },
    { key: "about_team4_img", label: "Member 4 Photo", type: "image" },
  ]},
  { group: "About · Sections", items: [
    { key: "about_editorial_title", label: "Editorial Heading", type: "text" },
    { key: "about_editorial_body", label: "Editorial Body (blank line between paragraphs)", type: "textarea", rows: 7 },
    { key: "about_trusted_title", label: "Trusted Partners Heading", type: "text" },
    { key: "about_trusted_body", label: "Trusted Partners Body", type: "textarea", rows: 6 },
    { key: "about_experience_title", label: "Experience Heading", type: "text" },
    { key: "about_experience_body", label: "Experience Body", type: "textarea", rows: 6 },
  ]},
  { group: "Listing Options Page", items: [
    { key: "listing_intro", label: "Intro Paragraph", type: "textarea", rows: 5 },
    { key: "listing_double_title", label: "Double Page · Title", type: "text" },
    { key: "listing_double_desc", label: "Double Page · Description", type: "textarea" },
    { key: "listing_double_highlights", label: "Double Page · Highlights (one per line)", type: "textarea" },
    { key: "listing_double_price", label: "Double Page · Price", type: "text" },
    { key: "listing_double_sample", label: "Double Page · Sample Image", type: "image" },
    { key: "listing_full_title", label: "Full Page · Title", type: "text" },
    { key: "listing_full_desc", label: "Full Page · Description", type: "textarea" },
    { key: "listing_full_highlights", label: "Full Page · Highlights (one per line)", type: "textarea" },
    { key: "listing_full_price", label: "Full Page · Price", type: "text" },
    { key: "listing_full_sample", label: "Full Page · Sample Image", type: "image" },
    { key: "listing_half_title", label: "Half Page · Title", type: "text" },
    { key: "listing_half_desc", label: "Half Page · Description", type: "textarea" },
    { key: "listing_half_highlights", label: "Half Page · Highlights (one per line)", type: "textarea" },
    { key: "listing_half_price", label: "Half Page · Price", type: "text" },
    { key: "listing_half_sample", label: "Half Page · Sample Image", type: "image" },
    { key: "listing_normal_title", label: "Normal · Title", type: "text" },
    { key: "listing_normal_desc", label: "Normal · Description", type: "textarea" },
    { key: "listing_normal_highlights", label: "Normal · Highlights (one per line)", type: "textarea" },
    { key: "listing_normal_price", label: "Normal · Price", type: "text" },
    { key: "listing_normal_sample", label: "Normal · Sample Image", type: "image" },
  ]},
  { group: "Listing Questionnaire", items: [
    { key: "questionnaire_form_url", label: "Google Form Embed URL", type: "text" },
  ]},
  { group: "Brochure / Media Kit", items: [
    { key: "media_kit_image_url", label: "Media Kit Image", type: "image" },
    { key: "media_kit_pdf_url", label: "Media Kit PDF (downloadable)", type: "file" },
  ]},
  { group: "Page Headings", items: [
    { key: "digital_title", label: "Digital Edition · Title", type: "text" },
    { key: "digital_subtitle", label: "Digital Edition · Subtitle", type: "text" },
    { key: "contact_title", label: "Contact · Title", type: "text" },
    { key: "contact_subtitle", label: "Contact · Subtitle", type: "textarea" },
    { key: "contact_office_heading", label: "Contact · Office Heading", type: "text" },
    { key: "questionnaire_title", label: "Questionnaire · Title", type: "text" },
    { key: "questionnaire_subtitle", label: "Questionnaire · Subtitle", type: "textarea" },
    { key: "listing_page_title", label: "Listing Options · Page Title", type: "text" },
    { key: "brochure_title", label: "Brochure · Title", type: "text" },
    { key: "brochure_subtitle", label: "Brochure · Subtitle", type: "textarea" },
    { key: "about_title", label: "About · Page Title", type: "text" },
  ]},
  { group: "Contact & Footer", items: [
    { key: "contact_company", label: "Company Name", type: "text" },
    { key: "contact_address", label: "Address (one line per row)", type: "textarea" },
    { key: "contact_email", label: "Contact Email", type: "text" },
    { key: "footer_text", label: "Footer Description Text", type: "textarea", rows: 5 },
  ]},
];

export default function SiteContent() {
  const { content, refresh } = useContentCtx();
  const [form, setForm] = useState(content);
  const [saving, setSaving] = useState(false);
  const [uploadingKey, setUploadingKey] = useState(null);
  const [openGroup, setOpenGroup] = useState("Hero Section");

  useEffect(() => { setForm(content); }, [content]);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const uploadImage = (key) => async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingKey(key);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, [key]: data.url }));
      toast.success("Image uploaded — remember to Save");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Upload failed");
    } finally {
      setUploadingKey(null);
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/content", { content: form });
      refresh();
      toast.success("Website content updated");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#faf9f6]" data-testid="admin-site-content">
      <header className="bg-white border-b border-slate-200 sticky top-0 z-40">
        <div className="max-w-5xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link to="/admin" className="inline-flex items-center gap-2 text-sm text-slate-600 hover:text-[#800020]" data-testid="content-back-btn">
            <ArrowLeft className="h-4 w-4" /> Back to Dashboard
          </Link>
          <button onClick={save} disabled={saving} className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-[#800020] hover:bg-[#660019] text-white font-medium transition-colors disabled:opacity-60" data-testid="content-save-btn">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save Changes
          </button>
        </div>
      </header>

      <div className="max-w-5xl mx-auto px-6 py-8">
        <div className="mb-6">
          <h1 className="font-serif text-3xl text-slate-900">Website Content</h1>
          <p className="text-slate-500 text-sm mt-1">Edit any text or image that appears on the public website. Click a section to expand.</p>
        </div>

        <div className="space-y-4">
          {FIELDS.map((g) => {
            const open = openGroup === g.group;
            return (
              <div key={g.group} className="bg-white rounded-xl border border-slate-200 overflow-hidden" data-testid={`content-group-${g.group.replace(/[^a-zA-Z0-9]+/g, "-")}`}>
                <button onClick={() => setOpenGroup(open ? "" : g.group)} className="w-full flex items-center justify-between px-6 py-4 text-left">
                  <h2 className="font-serif text-xl text-[#800020]">{g.group}</h2>
                  <span className="text-slate-400 text-sm">{open ? "−" : "+"}</span>
                </button>
                {open && (
                  <div className="px-6 pb-6 space-y-4 border-t border-slate-100 pt-4">
                    {g.items.map((f) => {
                      const Wrapper = RICH_KEYS.has(f.key) ? "div" : "label";
                      return (
                      <Wrapper key={f.key} className="block">
                        <span className="block text-sm font-medium text-slate-700 mb-1.5">{f.label}</span>
                        {RICH_KEYS.has(f.key) ? (
                          <RichTextEditor value={form[f.key] || ""} onChange={(html) => setForm((prev) => ({ ...prev, [f.key]: html }))} />
                        ) : f.type === "file" ? (
                          <div className="space-y-2">
                            <input value={form[f.key] || ""} onChange={set(f.key)} placeholder="PDF URL" className="w-full border border-slate-300 rounded-lg px-3 py-2 text-xs outline-none focus:border-[#800020]" data-testid={`content-field-${f.key}`} />
                            <div className="flex items-center gap-3">
                              <label className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg border border-slate-300 text-xs font-medium text-slate-700 hover:border-[#800020] cursor-pointer">
                                {uploadingKey === f.key ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5" />} Upload PDF
                                <input type="file" accept="application/pdf,.pdf" onChange={uploadImage(f.key)} className="hidden" data-testid={`content-upload-${f.key}`} />
                              </label>
                              {form[f.key] ? <a href={form[f.key]} target="_blank" rel="noreferrer" className="text-xs text-[#800020] underline">View current PDF</a> : <span className="text-xs text-slate-400">No PDF uploaded</span>}
                            </div>
                          </div>
                        ) : f.type === "textarea" ? (
                          <textarea value={form[f.key] || ""} onChange={set(f.key)} rows={f.rows || 3} className="w-full border border-slate-300 rounded-lg px-3.5 py-2.5 text-sm outline-none focus:border-[#800020]" data-testid={`content-field-${f.key}`} />
                        ) : f.type === "opacity" ? (
                          <div className="flex items-center gap-4">
                            <input type="range" min="0" max="100" value={Number(form[f.key]) || 0} onChange={(e) => setForm({ ...form, [f.key]: Number(e.target.value) })} className="flex-1 accent-[#800020]" data-testid={`content-field-${f.key}`} />
                            <span className="w-14 text-right text-sm text-slate-600">{Number(form[f.key]) || 0}%</span>
                          </div>
                        ) : f.type === "image" ? (
                          <div className="flex items-center gap-4">
                            <div className="h-20 w-20 rounded border border-slate-200 bg-slate-50 overflow-hidden flex items-center justify-center shrink-0">
                              {form[f.key] ? <img src={fileUrl(form[f.key])} alt="" className="h-full w-full object-contain" /> : <span className="text-[10px] text-slate-400">none</span>}
                            </div>
                            <div className="flex-1 space-y-2">
                              <input value={form[f.key] || ""} onChange={set(f.key)} placeholder="Image URL" className="w-full border border-slate-300 rounded-lg px-3 py-2 text-xs outline-none focus:border-[#800020]" data-testid={`content-field-${f.key}`} />
                              <label className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg border border-slate-300 text-xs font-medium text-slate-700 hover:border-[#800020] cursor-pointer">
                                {uploadingKey === f.key ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5" />} Upload
                                <input type="file" accept="image/*" onChange={uploadImage(f.key)} className="hidden" data-testid={`content-upload-${f.key}`} />
                              </label>
                            </div>
                          </div>
                        ) : (
                          <input value={form[f.key] || ""} onChange={set(f.key)} className="w-full border border-slate-300 rounded-lg px-3.5 py-2.5 text-sm outline-none focus:border-[#800020]" data-testid={`content-field-${f.key}`} />
                        )}
                      </Wrapper>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        <div className="mt-6">
          <button onClick={save} disabled={saving} className="inline-flex items-center gap-2 px-6 py-3 rounded-lg bg-[#800020] hover:bg-[#660019] text-white font-medium transition-colors disabled:opacity-60" data-testid="content-save-btn-bottom">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save Changes
          </button>
        </div>
      </div>
    </div>
  );
}
